import {
  ConnectionModeEnum,
  ConnectionStatusEnum,
  Direction,
  HeartBeatStatusEnum,
  includesEnumValue,
  isConnectionOnline,
  type Market,
  type MarketCredentialWithConnectionStatus,
} from '@talos/kyoko';
import type { DefaultTheme } from 'styled-components';

const UNHEALTHY_STATUSES = [ConnectionStatusEnum.Starting, ConnectionStatusEnum.Stopping, ConnectionStatusEnum.Error];

/**
 * Given a list of market and the current status of the heartbeat service,
 * return a status indicator bullet variant.
 *
 * @see HeartBeatStatusEnum
 * @see CONNECTION_STATUS_BULLET
 */
export const computeGlobalStatusColor = (
  marketCredentials: MarketCredentialWithConnectionStatus[],
  heartbeatStatus: string,
  theme: DefaultTheme
) => {
  if (heartbeatStatus === HeartBeatStatusEnum.OFFLINE) {
    return theme.colorTextNegative;
  }

  for (const market of marketCredentials) {
    const status = market.connectionStatus?.Status ?? ConnectionStatusEnum.Unavailable;
    if (includesEnumValue(UNHEALTHY_STATUSES, status)) {
      return theme.colorTextWarning;
    }
  }
  return theme.colorTextPositive;
};

/**
 * Given a list of market, returns a string on the form "(A / B)" where
 * A is the number of "connected" markets and B is the number of "configured" markets
 *
 * @see shouldIncrementNumerator
 * @see shouldIncrementDenominator
 */
export const computeNumConnectedMarkets = (markets: MarketCredentialWithConnectionStatus[]) => {
  let numerator = 0;
  let denominator = 0;
  for (const market of markets) {
    if (shouldIncrementNumerator(market)) {
      numerator++;
    }
    if (shouldIncrementDenominator(market)) {
      denominator++;
    }
  }

  return `${numerator}/${denominator}`;
};

/**
 * increment denominator as long as a market has a connection which is configured
 */
export const shouldIncrementDenominator = (market: MarketCredentialWithConnectionStatus) =>
  isConnConfiguredAndNotOffline(market);

/**
 * increment numerator unless a market has a connection
 * which is configured but not online
 */
export const shouldIncrementNumerator = (market: MarketCredentialWithConnectionStatus) =>
  isConnConfiguredAndNotOffline(market) && isHealthy(market);

const isHealthy = ({ connectionStatus }: MarketCredentialWithConnectionStatus) =>
  !!(
    connectionStatus &&
    !includesEnumValue(UNHEALTHY_STATUSES, connectionStatus.Status) &&
    [...Object.values(connectionStatus.ConnectionType)].every(s => s === true)
  );

export const isConnConfiguredAndNotOffline = ({ credential, connectionStatus }: MarketCredentialWithConnectionStatus) =>
  !!(
    credential.Mode === ConnectionModeEnum.Up &&
    connectionStatus !== undefined &&
    connectionStatus.Status !== ConnectionStatusEnum.Offline
  );

export type SortMode = 'Status' | 'Market' | 'Alphabetical';

export interface SortMarketCredentialsOptions {
  sortMode: SortMode;
  sortDirection: Direction;
  /** Required for 'Market' sort mode to get market display names */
  marketsByName?: Map<string, Market>;
}

/**
 * Sorts market credentials based on sort mode and direction:
 * - 'Status' mode: Non-online connections first, then alphabetically by label within each group
 * - 'Market' mode: Alphabetically by market display name
 * - 'Alphabetical' mode: Alphabetically by credential label
 */
export function sortMarketCredentialsByStatus(
  marketCredentials: MarketCredentialWithConnectionStatus[],
  options: SortMarketCredentialsOptions
): MarketCredentialWithConnectionStatus[] {
  const { sortMode, sortDirection, marketsByName } = options;
  const directionMultiplier = sortDirection === Direction.Asc ? 1 : -1;

  return [...marketCredentials].toSorted((a, b) => {
    // In 'Status' mode, non-online connections come first
    if (sortMode === 'Status') {
      const aIsOnline = isConnectionOnline(a.connectionStatus);
      const bIsOnline = isConnectionOnline(b.connectionStatus);

      if (aIsOnline !== bIsOnline) {
        // Non-online first (not affected by direction)
        return aIsOnline ? 1 : -1;
      }
    }

    // Determine sort key based on mode
    if (sortMode === 'Market') {
      // Sort by market display name, then by credential label
      const aMarket = marketsByName?.get(a.credential.Market)?.DisplayName ?? a.credential.Market;
      const bMarket = marketsByName?.get(b.credential.Market)?.DisplayName ?? b.credential.Market;
      const marketComparison = aMarket.localeCompare(bMarket);
      if (marketComparison !== 0) {
        return marketComparison * directionMultiplier;
      }
    }

    // Status and Alphabetical modes sort by credential label
    return a.credential.Label.localeCompare(b.credential.Label) * directionMultiplier;
  });
}
